<template>
  <div class="current-sale-line" :class="{ 'selected-line': isSelected }" @click="$emit('select-line', saleLine)" @dblclick="openItem">
    <div class="line-stock-code-pane">
      {{ saleLine.Plu }}
    </div>
    <div class="line-description-pane">
      <h6 class="line-description">
        {{ $utils.toPascalCase($utils.cutLength(saleLine.Description, 30)) }}
        <span v-if="saleLine.specialActive"
              class="special-badge"
              :title="saleLine.specialNote || 'Special active'"
              :style="{
                color: saleLine.specialType === 'discount' ? '#A529FF' : '#339CFF',
                borderColor: saleLine.specialType === 'discount' ? '#A529FF' : '#339CFF'
              }">
          Special
        </span>
      </h6>
    </div>
    <div class="line-tax-pane">
      <div class="d-flex flex-column">
        <span class="line-price" v-if="saleLine.tourismLevy > 0">{{ $utils.formatMoney(saleLine.tourismLevy * saleLine.Qty, 2, '.', ',', false) }}</span>
        <span class="tax-label" v-if="taxLabel">{{ taxLabel }}</span>
        <span v-if="!saleLine.tourismLevy && !taxLabel">-</span>
      </div>
    </div>

    <div class="line-qty-pane">
      <button
        type="button"
        class="qty-btn"
        aria-label="Decrease quantity"
        :disabled="Number(saleLine.Qty) <= 0"
        @click.stop="decreaseQty"
      >
        -
      </button>
      <input
        type="number"
        class="qty-value"
        v-model.number="saleLine.Qty"
        :readonly="isQtyReadOnly"
        @click.stop
        @change="onQtyChange"
        @blur="syncQtyFromStore"
        min="0"
        step="1"
      />
      <button
        type="button"
        class="qty-btn"
        aria-label="Increase quantity"
        @click.stop="increaseQty"
      >
        +
      </button>
    </div>
    <div class="line-discount-pane">
      <span class="line-price" v-if="saleLine.Discount > 0">{{ $utils.formatMoney(saleLine.Discount, 2, '.', ',', false) }}</span>
      <span v-else>-</span>
    </div>
    <div class="line-unit-price-pane">
      <span class="line-price">{{ $utils.formatMoney(saleLine.Unit, 2, '.', ',', false) }}</span>
    </div>


    <div class="line-total-price-pane">
      <span class="line-price">{{ $utils.formatMoney(( (saleLine.Unit + saleLine.tourismLevy) * saleLine.Qty) - saleLine.Discount + instructionTotal, 2, '.', ',', false) }}</span>
      <div class="d-flex flex-column" v-if="saleCurrency && saleCurrency.Symbol && !(saleCurrency.Rate===1)">
        <span class="text-primary text-sm">Values in ({{ saleCurrency.Symbol }})</span>
        <div class="currency-values">
          <span class="line-price">{{ $utils.formatMoney(saleLine.Unit / saleCurrency.Rate, 2, '.', ',', false) }}</span>
          <span class="line-price">{{ $utils.formatMoney(((saleLine.Unit * saleLine.Qty) - saleLine.Discount + instructionTotal) / saleCurrency.Rate, 2, '.', ',', false) }}</span>
        </div>
      </div>
    </div>
    <div class="line-delete-pane">
      <button
        type="button"
        class="delete-btn"
        aria-label="Delete item"
        @click.stop="deleteItem"
      >
        <font-awesome-icon icon="fa-solid fa-trash"/>
      </button>
    </div>
    <div class="line-instructions-pane" v-if="hasInstructions">
      <ul class="cooking-instruction-list">
        <li class="cooking-instruction-list-item" v-for="instruction in instructions" :key="`${saleLine.index}-${instruction.CookingMsg}-${instruction.Price}`">
          <span>{{ instruction.CookingMsg }}</span>
          <span>{{ instruction.count }} X {{ $utils.formatMoney(instruction.Price) }} = {{ $utils.formatMoney(instruction.count * instruction.Price) }}</span>
        </li>
      </ul>
    </div>
    <!-- Update Price Modal - Brand New Implementation -->
    <custom-modal :ref="`saleline-item-`+saleLine.index" size="md"
                  :title="`Update Item Price - ${$utils.toPascalCase(saleLine.Description)}`">
      <div class="d-flex flex-column gap-3">

        <!-- Permission notice: visible only when the cashier lacks the PriceChange permission -->
        <div v-if="!canChangePrice" class="alert alert-warning py-2 px-3 mb-0">
          Price changes require additional permission. Quantity can still be edited.
        </div>

        <!-- Price Level Selection -->
        <div v-if="canSetPriceLevel">
          <label :for="`price-level-${saleLine.index}`" class="form-label">Price Level</label>
          <select :id="`price-level-${saleLine.index}`" class="form-control"
                  v-model="editData.priceLevel"
                  :disabled="!canChangePrice"
                  @change="onPriceLevelChange">
            <option value="Normal">Normal Price</option>
            <option value="Price 1">Price 1</option>
            <option value="Price 2">Price 2</option>
            <option value="Price 3">Price 3</option>
            <option value="Price 4">Price 4</option>
            <option value="Price 5">Price 5</option>
            <option value="Custom">Custom Price</option>
          </select>
        </div>

        <!-- Unit Price -->
        <div>
          <label :for="`unit-price-${saleLine.index}`" class="form-label">Unit Price</label>
          <input type="number" step="0.01" :id="`unit-price-${saleLine.index}`"
                 class="form-control"
                 v-model.number="editData.unitPrice"
                 @input="onUnitPriceChange"
                 :readonly="!canChangePrice"
                 @focus="$event.target.select()">
        </div>

        <!-- Quantity -->
        <div>
          <label :for="`quantity-${saleLine.index}`" class="form-label">Quantity</label>
          <div class="input-group">
            <input type="number" step="0.001" :id="`quantity-${saleLine.index}`"
                   class="form-control"
                   v-model.number="editData.quantity"
                   @input="onQuantityChange"
                   :readonly="isScaleItem && !canManualWeightEntry"
                   :class="{ 'manual-weight-locked': isScaleItem && !canManualWeightEntry }"
                   :ref="el => setQuantityInputRef(el)"
                   @focus="$event.target.select()">
            <button v-if="isElectron" class="btn btn-secondary-lightning" type="button" 
                    @click="readFromScale" :disabled="readingScale">
              <span v-if="readingScale" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              <span v-else>Read Scale</span>
            </button>
          </div>
          <div v-if="isScaleItem && !canManualWeightEntry" class="alert alert-warning py-2 px-3 mt-2 mb-0">
            Manual weight entry is disabled for this cashier. Use <strong>Read Scale</strong> to set the quantity.
          </div>
        </div>

        <!-- Discount Info -->
        <div v-if="saleLine.Discount > 0" class="alert alert-info">
          <small class="d-block">Line Before Discount: {{ $utils.formatMoney(editData.linePrice + saleLine.Discount) }}</small>
          <small class="d-block text-success">Discount Applied: {{ $utils.formatMoney(saleLine.Discount) }}</small>
        </div>

        <!-- Line Price -->
        <div>
          <label :for="`line-price-${saleLine.index}`" class="form-label">Line Price (Total)</label>
          <input type="number" step="0.01" :id="`line-price-${saleLine.index}`"
                 class="form-control"
                 v-model.number="editData.linePrice"
                 @input="onLinePriceChange"
                 :readonly="!canChangePrice"
                 @focus="$event.target.select()">
        </div>

        <!-- Currency Conversion -->
        <div v-if="saleCurrency && !(saleCurrency.Rate === 1)">
          <label :for="`currency-price-${saleLine.index}`" class="form-label">
            Line Price ({{ saleCurrency.Symbol }})
          </label>
          <input type="number" step="0.001" :id="`currency-price-${saleLine.index}`"
                 class="form-control"
                 v-model.number="editData.currencyPrice"
                 @input="onCurrencyPriceChange"
                 :readonly="!canChangePrice"
                 @focus="$event.target.select()">
        </div>

        <!-- Action Buttons
             `sticky-action-row` pins Save Changes / Cancel to the bottom of the
             scrolling modal-body on small (mobile / Capacitor / phone-sized)
             viewports so cashiers can always commit or cancel — see the
             `.sticky-action-row` rule in the scoped <style> block below and
             exec-plan 2026-07-17-mobile-save-button-update-price-modal. -->
        <div class="d-flex justify-content-end gap-2 mt-3 sticky-action-row">
          <button type="button" class="btn btn-secondary btn-secondary-lightning"
                  @click="closeModal">Cancel</button>
          <button type="button" class="btn btn-primary btn-primary-lightning"
                  @click="saveChanges">Save Changes</button>
        </div>

      </div>
    </custom-modal>
    <!-- Hidden button for triggering discount from parent (CurrentSaleTable) -->
    <button type="button" style="display: none" :id="`discountCheck-${saleLine.index}`" @click="discount"></button>
    <!-- Modal for Discount Input -->
    <custom-modal :ref="`discount-modal-${saleLine.index}`" size="sm" :title="`Apply Discount to ${$utils.toPascalCase($utils.cutLength(saleLine.Description, 30))}`">
      <form @submit.prevent="applyDiscount">
        <div class="d-flex flex-row p-1 justify-content-evenly mt-2">
          <label>
            <input class="form-check-input" type="radio" value="value" v-model="discountType">
            Value Discount
          </label>
          <label>
            <input class="form-check-input" type="radio" value="percent" v-model="discountType">
            Percentage Discount
          </label>
        </div>
        <hr class="mx-2"/>
        <label class="row mx-3 mb-2">
          Discount Amount
          <input type="number" class="form-control" v-model="discountAmount" @focus="$event.target.select()">
        </label>
        <div class="d-flex justify-content-end mt-3">
          <button type="button" class="btn btn-secondary btn-secondary-lightning me-2" @click="$refs[`discount-modal-${saleLine.index}`].closeModal()">Close</button>
          <button type="submit" class="btn btn-primary btn-primary-lightning">Save changes</button>
        </div>
      </form>
    </custom-modal>
  </div>
</template>

<script>
import LineSeparator from "@/components/layout/utils/LineSeparator";
import CustomModal from "@/components/layout/utils/CustomModal";
import scaleService from "@/services/scaleService";

export default {
  name: "CurrentSaleLine",
  async mounted() {
    this.canSetPriceLevel = await this.$store.dispatch(`validateSecurityPermissionNoPrompt`, `PriceLevel`);
  },
  components: {CustomModal, LineSeparator},
  data() {
    return {
      canSetPriceLevel: false,
      // Reactive flag derived from the `PriceChange` security permission, evaluated
      // each time the Update Price modal is opened. Drives whether the price-related
      // inputs (unit price, line price, currency price, price level) are read-only
      // vs. fully editable. Quantity stays editable regardless — see openItem().
      canChangePrice: false,
      canManualWeightEntry: true,
      isScaleItem: false,
      scaleReadApplied: false,
      readingScale: false,
      isElectron: false,
      discountType: 'value',
      discountAmount: 0,
      // Ref handle for the Quantity input inside the Update Price modal.
      // Used to auto-focus & select the field right after the modal opens,
      // since quantity is the value cashiers change most often.
      quantityInputEl: null,
      // Edit data for modal - prevents race conditions
      editData: {
        priceLevel: '',
        unitPrice: 0,
        quantity: 0,
        linePrice: 0,
        currencyPrice: 0
      }
    }
  },
  created() {
      this.isElectron = !!(window.electronAPI);
  },
  props: {
    saleLine: {
      type: Object,
      required: true
    },
    isSelected: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    deviceSettings() {
      return this.$store.getters.getDeviceSettings;
    },
    isQtyReadOnly() {
      if (!this.deviceSettings) return true;
      return !this.deviceSettings.SelectQty;
    },
    hasInstructions() {
      return (this.saleLine.instructions && this.saleLine.instructions.length > 0)
    },
    saleCurrency() {
      return this.$store.getters.getSaleCurrency;
    },
    taxLabel() {
      // Get the tax label for the current sale line's PLU
      const stockItem = this.$store.getters.getByPlu(this.saleLine.Plu);
      if (stockItem && stockItem.TaxLabels) {
        // Get the tax category from the tax module
        const taxCategory = this.$store.getters.getZraCategoryByLabel(stockItem.TaxLabels);
        return taxCategory ? taxCategory.TaxLabel : '';
      }
      return '';
    },
    instructionTotal() {
      if (!this.saleLine.instructions || this.saleLine.instructions.length === 0) return 0.00;
      let total = this.saleLine.instructions
        .map(i => Number(i.Price || 0) * Number(i.count || i.Qty || 1))
        .reduce((acc, n) => Number(n) + Number(acc), 0);
      if (isNaN(total)) {
        return 0.00;
      }
      return Number(total.toFixed(2))
    },
    instructions() {
      return this.saleLine.instructions.map((int) => {
        int.Price = Number(int.Price.toFixed(2))
        return int;
      });
    }
  },
  methods: {
    /**
     * Ref callback for the Quantity input inside the Update Price modal.
     * Captures the input element so openItem() can focus/select it once the
     * modal is fully shown.
     * @param {HTMLElement|null} el
     */
    setQuantityInputRef(el) {
      this.quantityInputEl = el;
    },
    onQtyChange() {
      if (this.isQtyReadOnly) return;
      this.$store.commit('updateSaleLineQty', {
        saleLine: this.saleLine,
        qty: this.saleLine.Qty
      });
    },
    syncQtyFromStore() {
      if (this.isQtyReadOnly) return;
      const line = this.$store.state.sale.currentSaleLines.find(l => l.index === this.saleLine.index);
      if (line && line.Qty !== this.saleLine.Qty) {
        this.saleLine.Qty = line.Qty;
      }
    },
    deleteItem() {
      this.$store.dispatch(`validateSecurityPermissionNoPrompt`, `Void`).then(isAllowed => {
        if (!isAllowed) return;
        this.$store.commit('voidLine', this.saleLine);
      });
    },
    // ===== NEW MODAL METHODS =====

    /**
     * When price level changes, fetch and update with predefined price
     */
    async onPriceLevelChange() {
      if (this.editData.priceLevel === 'Custom') {
        // User selected custom - keep current unit price
        return;
      }

      // Get stock item to fetch predefined prices
      const stockItem = this.$store.getters.getByPlu(this.saleLine.Plu);
      if (!stockItem) return;

      let newUnitPrice = stockItem.Unit; // Default Normal price

      // Map price level to stock item price
      switch (this.editData.priceLevel) {
        case 'Price 1':
          newUnitPrice = stockItem.Price1 || stockItem.Unit;
          break;
        case 'Price 2':
          newUnitPrice = stockItem.Price2 || stockItem.Unit;
          break;
        case 'Price 3':
          newUnitPrice = stockItem.Price3 || stockItem.Unit;
          break;
        case 'Price 4':
          newUnitPrice = stockItem.Price4 || stockItem.Unit;
          break;
        case 'Price 5':
          newUnitPrice = stockItem.Price5 || stockItem.Unit;
          break;
        default: // Normal
          newUnitPrice = stockItem.Unit;
      }

      // Update unit price and recalculate line
      this.editData.unitPrice = Number(newUnitPrice);
      this.calculateLineFromUnitAndQty();
    },

    /**
     * When unit price changes, recalculate line price
     */
    onUnitPriceChange() {
      this.editData.priceLevel = 'Custom';
      this.calculateLineFromUnitAndQty();
    },

    /**
     * When quantity changes, recalculate line price
     */
    onQuantityChange() {
      this.calculateLineFromUnitAndQty();
    },

    /**
     * When line price changes, recalculate unit price
     */
    onLinePriceChange() {
      this.calculateUnitFromLine();
    },

    /**
     * When currency price changes, update line price
     */
    onCurrencyPriceChange() {
      if (!this.saleCurrency) return;
      this.editData.linePrice = Number((this.editData.currencyPrice * this.saleCurrency.Rate).toFixed(2));
      this.calculateUnitFromLine();
    },

    /**
     * Calculate line price from unit price and quantity
     */
    calculateLineFromUnitAndQty() {
      const unit = Number(this.editData.unitPrice) || 0;
      const qty = Number(this.editData.quantity) || 0;
      const discount = Number(this.saleLine.Discount) || 0;
      
      this.editData.linePrice = Number(((unit * qty) - discount).toFixed(2));
      
      // Update currency price if applicable
      if (this.saleCurrency && this.saleCurrency.Rate !== 1) {
        this.editData.currencyPrice = Number((this.editData.linePrice / this.saleCurrency.Rate).toFixed(3));
      }
    },

    /**
     * Calculate unit price from line price
     */
    calculateUnitFromLine() {
      const line = Number(this.editData.linePrice) || 0;
      const qty = Number(this.editData.quantity) || 1; // Avoid division by zero
      const discount = Number(this.saleLine.Discount) || 0;
      
      this.editData.unitPrice = Number(((line + discount) / qty).toFixed(2));
      this.editData.priceLevel = 'Custom';
      
      // Update currency price if applicable
      if (this.saleCurrency && this.saleCurrency.Rate !== 1) {
        this.editData.currencyPrice = Number((this.editData.linePrice / this.saleCurrency.Rate).toFixed(3));
      }
    },

    /**
     * Save changes to the actual saleLine
     */
    saveChanges() {
      if (this.isScaleItem && !this.canManualWeightEntry && !this.scaleReadApplied && Number(this.editData.quantity) !== Number(this.saleLine.Qty)) {
        window.Toast.fire({
          icon: 'warning',
          title: 'Manual weight entry is disabled for this cashier. Use Read Scale.'
        });
        return;
      }

      // Commit to store to update totals and keep instruction counts in sync
      const payload = {
        ...this.saleLine,
        priceType: this.editData.priceLevel,
        Unit: Number(this.editData.unitPrice),
        Qty: Number(this.editData.quantity),
        Line: Number(this.editData.linePrice),
      };
      this.$store.commit('updateSaleLinePrice', payload);

      // Close modal
      this.closeModal();
    },

    /**
     * Close modal without saving. Resets `canChangePrice` defensively so that
     * the next `openItem()` call re-evaluates the `PriceChange` permission
     * rather than relying on a stale value from a previous session.
     */
    closeModal() {
      this.canChangePrice = false;
      this.$refs[`saleline-item-${this.saleLine.index}`].closeModal();
    },

    /**
     * Open the Update Price modal for this sale line. Called from the parent table
     * when the cashier double-clicks the row. We evaluate the `PriceChange` security
     * permission and store the result in `this.canChangePrice` instead of bailing
     * out when permission is denied — the modal still opens so the cashier can fix
     * a wrong quantity (which is not a price change), but every price-related
     * input is bound `:readonly="!canChangePrice"` and a visible warning notice
     * explains why the price fields are locked. The price level `<select>` is
     * disabled when permission is denied since a read-only `<select>` is
     * meaningless.
     */
    async openItem() {
      this.canChangePrice = await this.$store.dispatch(`validateSecurityPermissionNoPrompt`, `PriceChange`);
      
      // Check if scale should be available for this item
      await this.checkScaleAvailability();

      // Initialize editData from current saleLine values
      this.editData = {
        priceLevel: this.saleLine.priceType || 'Custom',
        unitPrice: Number(this.saleLine.Unit) || 0,
        quantity: Number(this.saleLine.Qty) || 0,
        linePrice: Number(this.saleLine.Line) || 0,
        currencyPrice: this.saleCurrency ? Number(this.saleLine.Line / this.saleCurrency.Rate) : 0
      };
      this.scaleReadApplied = false;

      // Wait for Bootstrap to fully show the modal, then auto-focus & select the Quantity
      // input. We must wait for `shown.bs.modal` (not just `nextTick`) because Bootstrap's
      // own focus handler runs after the animation completes and would otherwise steal
      // focus back to the modal element.
      // Quantity is the field cashiers most often need to change, so pre-selecting it
      // lets them start typing a new value immediately after the modal opens.
      // Note: CustomModal generates a random UUID for its DOM id, so we can't use a
      // stable id like `saleline-item-${index}-customModal`. Instead we resolve the
      // modal element from the Vue component ref's $el wrapper.
      const modalRef = this.$refs[`saleline-item-${this.saleLine.index}`];
      const wrapperEl = Array.isArray(modalRef) ? (modalRef[0] && modalRef[0].$el) : (modalRef && modalRef.$el);
      const modalEl = wrapperEl ? wrapperEl.querySelector('.modal') : null;

      const onShown = () => {
        if (this.quantityInputEl) {
          this.quantityInputEl.focus();
          this.quantityInputEl.select();
        } else {
          // Fallback: try to find by id (in case the ref wasn't set in time)
          const qtyById = document.getElementById(`quantity-${this.saleLine.index}`);
          if (qtyById) {
            qtyById.focus();
            qtyById.select();
          }
        }
        if (modalEl) modalEl.removeEventListener('shown.bs.modal', onShown);
      };

      if (modalEl) {
        modalEl.addEventListener('shown.bs.modal', onShown);
      } else {
        // Last-resort fallback: listen on body and verify the event's target is a modal.
        // This is rare — only happens if the ref isn't mounted yet.
        const onBodyShown = (e) => {
          if (e.target && e.target.classList && e.target.classList.contains('modal')) {
            onShown();
            document.body.removeEventListener('shown.bs.modal', onBodyShown);
          }
        };
        document.body.addEventListener('shown.bs.modal', onBodyShown);
      }

      await this.$refs[`saleline-item-${this.saleLine.index}`].openModal();
    },

    // ===== SCALE READING =====
    
    async readFromScale() {
      if (this.readingScale) return;
      
      this.readingScale = true;
      try {
        // First ensure we're connected
        const ports = await scaleService.listPorts();
        console.log('Available ports:', ports);

        // First check local_scale_settings (same as other components)
        let port = '';
        let baud = 9600;
        let dataBits = 8;
        let parity = 'none';
        let scaleModel = 'standard';
        let scaleDecimal = 3;
        let scaleUnit = 'kg';

        try {
            const localSettings = localStorage.getItem('local_scale_settings');
            if (localSettings) {
                const config = JSON.parse(localSettings);
                if (config.ScalePort) port = config.ScalePort;
                if (config.ScaleBaud) baud = parseInt(config.ScaleBaud);
                if (config.ScaleDataBits) dataBits = parseInt(config.ScaleDataBits);
                if (config.ScaleParity) parity = config.ScaleParity;
                if (config.ScaleModel) scaleModel = config.ScaleModel;
                if (config.ScaleDecimal) scaleDecimal = parseInt(config.ScaleDecimal);
                if (config.ScaleUnit) scaleUnit = config.ScaleUnit;
            }
        } catch (e) {
            console.error('Error loading local scale settings:', e);
        }

        // Fallback to global settings if not set
        const deviceSettings = this.$store.getters.getDeviceSettings || {};
        if (!port && deviceSettings.ScalePort) port = deviceSettings.ScalePort;
        if (!baud && deviceSettings.ScaleBaud) baud = parseInt(deviceSettings.ScaleBaud);
        if (!dataBits && deviceSettings.ScaleDataBits) dataBits = parseInt(deviceSettings.ScaleDataBits);
        if (!parity && deviceSettings.ScaleParity) parity = deviceSettings.ScaleParity;
        if (!scaleModel && deviceSettings.ScaleModel) scaleModel = deviceSettings.ScaleModel;
        if (!scaleDecimal && deviceSettings.ScaleDecimal) scaleDecimal = parseInt(deviceSettings.ScaleDecimal);
        if (!scaleUnit && deviceSettings.ScaleUnit) scaleUnit = deviceSettings.ScaleUnit;

        const settings = {
            comPort: port || 'COM2',
            baudRate: baud,
            dataBits: dataBits,
            parity: parity,
            scaleModel: scaleModel,
            scaleDecimal: scaleDecimal
        };

        console.log('[CurrentSaleLine] === SCALE SETTINGS ===');
        console.log('[CurrentSaleLine] Port:', settings.comPort, '| Model:', settings.scaleModel, '| Decimal:', settings.scaleDecimal);
        console.log('[CurrentSaleLine] Local Settings:', localStorage.getItem('local_scale_settings'));

        // Try to connect (scale service handles reconnection if needed)
        const connRes = await scaleService.connect(settings.comPort, settings.baudRate, settings.dataBits, settings.parity, 1, settings.scaleModel, settings.scaleDecimal);
        if (!connRes.ok && connRes.error !== 'Port already open') { 
           console.warn('Connection warning:', connRes.error);
           if (!connRes.ok) throw new Error(connRes.error);
        }

        // Read weight
        const result = await scaleService.readWeight();
        
        if (result.ok) {
           const scaleUnit = this.getScaleUnitSetting();
           let newWeight = parseFloat(result.weight);
           if (scaleUnit === 'g') {
             newWeight = newWeight / 1000;
           }
            
           // Update editData quantity
           this.editData.quantity = newWeight;
           this.scaleReadApplied = true;
           
           // Recalculate line price
           this.calculateLineFromUnitAndQty();
           
           window.Toast.fire({
             icon: 'success',
             title: `Weight read: ${newWeight} kg`
           });
        } else {
           throw new Error(result.error);
        }
      } catch (err) {
        console.error('Scale read error:', err);
        window.Toast.fire({
          icon: 'error',
          title: 'Failed to read from scale: ' + err.message
        });
      } finally {
        this.readingScale = false;
      }
    },
    
    async checkScaleAvailability() {
        const getByPlu = this.$store.getters.getStockSettingByPlu;
        const cachedSetting = typeof getByPlu === 'function' ? getByPlu(this.saleLine.Plu) : null;
        const defaultSetting = {
          scale: false
        };
        const set = cachedSetting || await this.$store.dispatch('getStockSetting', this.saleLine.Plu) || defaultSetting;
        this.isScaleItem = Boolean(set && (set.scale || set.Scale));
        this.canManualWeightEntry = await this.$store.dispatch(`validateSecurityPermissionNoPrompt`, `manualWeightEntry`);

        return this.isScaleItem;
    },

    getScaleUnitSetting() {
      let unit = 'kg';
      let unitFromLocal = false;
      try {
        const localSettings = localStorage.getItem('local_scale_settings');
        if (localSettings) {
          const config = JSON.parse(localSettings);
          if (config.ScaleUnit) {
            unit = config.ScaleUnit;
            unitFromLocal = true;
          }
        }
      } catch (e) {
        console.warn('Failed to read local scale unit setting', e);
      }

      if (!unitFromLocal) {
        const deviceSettings = this.$store.getters.getDeviceSettings || {};
        if (deviceSettings.ScaleUnit) unit = deviceSettings.ScaleUnit;
      }

      return unit;
    },

    // ===== DISCOUNT METHODS (unchanged) =====
    
    voidItem(item) {
      this.$store.dispatch(`validateSecurityPermissionNoPrompt`, `Void`).then(isAllowed => {
        if (!isAllowed) return;
        this.$store.commit('voidLine', item)
      });
    },
    
    async discount() {
      let isAllowed = await this.$store.dispatch(`validateSecurityPermission`, `Discount`)
      if (!isAllowed) return;
      this.$refs[`discount-modal-${this.saleLine.index}`].openModal();
    },
    
    /**
     * Apply a discount (value or percentage) to the selected sale line.
     *
     * The percentage base mirrors the displayed line total so cooking
     * instructions (and tourism levy) participate in the discount:
     *
     *   line = (Unit + tourismLevy) * Qty + instructionTotal
     *
     * Without including instructionTotal, a 10% discount on a line with
     * instructions was effectively only applied to the base product
     * (Felix, 2026-06-23 - "discount not including instructions").
     *
     * We deliberately do NOT mutate `saleLine.Line` here. The display
     * formula `(Unit + tourismLevy) * Qty - Discount + instructionTotal`
     * already accounts for the discount, and `Line` is read by the
     * "Update Price" modal as the basis for `editData.linePrice` —
     * mutating it on every discount skewed subsequent price edits.
     *
     * @returns {void}
     */
    applyDiscount() {
      const qty = Number(this.saleLine.Qty) || 0;
      const unit = Number(this.saleLine.Unit) || 0;
      const levy = Number(this.saleLine.tourismLevy || 0);
      const instructionTotal = Number(this.instructionTotal) || 0;
      const line = ((unit + levy) * qty) + instructionTotal;

      const entered = Number(this.discountAmount) || 0;
      let discount;
      if (this.discountType === `percent`) {
        discount = (entered / 100) * line;
      } else {
        // Value discount: cap at the line total so the line can never
        // go negative if a cashier types more than the line is worth.
        discount = Math.min(entered, line);
      }
      this.saleLine.Discount = Number(discount.toFixed(2));
      this.$refs[`discount-modal-${this.saleLine.index}`].closeModal();
    },

    increaseQty() {
      this.$emit('increase-qty-line', this.saleLine);
    },

    decreaseQty() {
      if ((Number(this.saleLine.Qty) || 0) <= 0) return;
      this.$emit('decrease-qty-line', this.saleLine);
    },
  }
}
</script>

<style lang="scss" scoped>
/* Modal theming and spacing for per-line discount (no layout/size changes) */
.modal-content.card-base {
  background: var(--card-bg);
  border: var(--card-border);
  box-shadow: var(--card-shadow);
  border-radius: 12px;
}
.modal-header, .modal-body, .modal-footer {
  background: var(--card-bg);
  color: var(--text-primary);
}
.modal-title { color: var(--text-primary); }
.form-control {
  background: var(--card-bg);
  color: var(--text-primary);
  border: var(--card-border);
  border-radius: 12px;
  padding: 8px 12px; /* input text padding */
}

.manual-weight-locked {
  background: rgba(51, 156, 255, 0.08);
  cursor: not-allowed;
}
.btn-primary-lightning {
  background: var(--primary-button-bg);
  color: var(--primary-button-text);
  border: none;
  box-shadow: var(--card-shadow);
}
.btn-primary-lightning:hover {
  background: var(--primary-button-hover);
  color: var(--primary-button-text);
}
.btn-secondary-lightning {
  background: var(--secondary-button-bg);
  color: var(--secondary-button-text);
  border: 1px solid rgba(var(--accent-electric-blue-rgb), 0.15);
  box-shadow: var(--card-shadow);
}
.btn-secondary-lightning:hover {
  background: var(--secondary-button-bg-dark);
  color: var(--text-primary);
}

.current-sale-line {
  display: flex; /* Use flexbox for layout */
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center; /* Vertically align items */
  background: transparent;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  min-height: 35px; /* Minimum height */
  border-bottom: 1px solid rgba(255, 255, 255, 0.05); /* Separator */

  &.selected-line {
    background-color: rgba(51, 156, 255, 0.2) !important;
    border-left: 3px solid var(--electric-blue);

    @media (prefers-color-scheme: dark) {
      background-color: rgba(51, 156, 255, 0.3) !important;
    }
  }

  td {
    padding: 2px 6px; /* Reduced vertical padding */
    vertical-align: middle;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 1200px) {
      padding: 2px 5px;
    }
    @media (max-width: 992px) {
      padding: 1px 4px;
    }
    @media (max-width: 768px) {
      padding: 1px 3px;
    }
  }

  .line-stock-code-pane {
    width: 10%;
    text-align: left;
    padding-left: 12px;
    font-size: 13px;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 1200px) {
      font-size: 12px;
    }
    @media (max-width: 992px) {
      font-size: 11px;
    }
    @media (max-width: 768px) {
      font-size: 10px;
    }
  }

  .line-description-pane {
    width: 30%;
    text-align: left;
    padding-left: 12px;

    .line-description {
      font-size: 13px;
      margin: 0;
      line-height: 1.2;
      font-weight: 500;
      color: var(--text-primary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      .special-badge {
        display: inline-block;
        margin-left: 8px; /* Button spacing: 12px ideal but keep compact inside line */
        padding: 2px 6px; /* Button text padding: 8px guideline, reduced to avoid layout change */
        font-size: 11px;
        border: 1px solid;
        border-radius: 12px;
        background: transparent;
        box-shadow: 0 2px 8px rgba(0,0,0,0.08); /* subtle shadow per guidelines */
      }

      @media (max-width: 1200px) {
        font-size: 12px;
      }
      @media (max-width: 992px) {
        font-size: 11px;
      }
      @media (max-width: 768px) {
        font-size: 10px;
      }
    }

    .text-sm {
      font-size: 10px;
      line-height: 1.1;
      color: var(--text-secondary);

      @media (max-width: 768px) {
        font-size: 9px;
      }
    }

  }

  .line-qty-pane {
    width: 8%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);

    .qty-value {
      min-width: 36px;
      max-width: 50px;
      text-align: center;
      line-height: 1;
      background: transparent;
      border: 1px solid transparent;
      border-radius: 4px;
      padding: 2px 4px;
      font-size: inherit;
      font-weight: inherit;
      color: inherit;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .qty-value:focus {
      background: var(--card-bg);
      border-color: var(--accent-electric-blue);
      outline: none;
    }

    .qty-value::-webkit-outer-spin-button,
    .qty-value::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    .qty-value[type="number"] {
      -moz-appearance: textfield;
    }

    .qty-btn {
      width: 16px;
      height: 16px;
      min-width: 16px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 1px solid var(--card-border);
      border-radius: 50%;
      padding: 0;
      line-height: 1;
      font-size: 11px;
      background: transparent;
      color: var(--text-primary);
    }

    .qty-btn:hover:not(:disabled) {
      border-color: var(--accent-electric-blue);
      background: rgba(var(--accent-electric-blue-rgb), 0.15);
    }

    .qty-btn:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    @media (max-width: 1200px) {
      font-size: 12px;
    }
    @media (max-width: 992px) {
      font-size: 11px;
    }
    @media (max-width: 768px) {
      font-size: 10px;

      .qty-value {
        min-width: 20px;
      }

      .qty-btn {
        width: 14px;
        height: 14px;
        min-width: 14px;
      }
    }

  }

  .line-unit-price-pane {
    width: 12%;
    padding-right: 12px;
    text-align: right;
  }

  .line-tax-pane {
    width: 10%;
    padding-right: 12px;
    text-align: right;

    .tax-label {
      font-size: 11px;
      color: var(--text-secondary);
      font-weight: 500;

      @media (max-width: 768px) {
        font-size: 10px;
      }
    }
  }

  .line-discount-pane {
    width: 10%;
    padding-right: 12px;
    text-align: right;
  }

  .line-total-price-pane {
    width: 12%;
    padding-right: 12px;
    text-align: right;
  }

  .line-delete-pane {
    width: 5%;
    display: flex;
    align-items: center;
    justify-content: center;

    .delete-btn {
      background: transparent;
      border: 1px solid var(--card-border);
      border-radius: 4px;
      padding: 4px 8px;
      color: var(--text-secondary);
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        color: #e53e3e;
        border-color: #e53e3e;
        background: rgba(229, 62, 62, 0.1);
      }
    }
  }

  .line-instructions-pane {
    flex: 0 0 100%;
    width: 100%;
    padding: 2px 12px 6px;
    color: var(--text-secondary);
    border-top: 1px dashed rgba(255, 255, 255, 0.06);
    margin-top: 2px;
  }

  .line-price {
    font-size: 13px;
    font-weight: 600;
    color: var(--electric-blue);

    @media (max-width: 1200px) {
      font-size: 12px;
    }
    @media (max-width: 992px) {
      font-size: 11px;
    }
    @media (max-width: 768px) {
      font-size: 10px;
    }
  }

}

.cooking-instruction-list {
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
  margin-top: 0;

  .cooking-instruction-list-item {
    font-size: 10px;
    font-weight: 600;
    color: var(--text-secondary);
    display: flex;
    justify-content: space-between;
    gap: 8px;
    line-height: 1.2;

    @media (max-width: 768px) {
      font-size: 9px;
    }
  }
}

.price-row {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.price-row-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 1px; /* Reduced margin for more compact layout */
  line-height: 1; /* Tighter line height */
}

.currency-values {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  margin-top: 2px;
}

.text-sm {
  font-size: 12px;

  @media (max-width: 768px) {
    font-size: 10px;
  }
}

#discount-btn {
  padding: 2px;
  border-radius: 5px;
}

/* Ensure discount modal radios and amount input adapt to light/dark mode */
.modal-content.card-base .form-check-input {
  background-color: var(--card-bg);
  border: var(--card-border);
  accent-color: var(--accent-electric-blue);
}
.modal-content.card-base .form-check-input:focus {
  outline: none;
  border-color: var(--accent-electric-blue);
  box-shadow: 0 0 0 0.2rem rgba(var(--accent-electric-blue-rgb), 0.25);
}
.modal-content.card-base .form-check-input:hover {
  border-color: var(--accent-electric-blue);
}

/* Radio labels within the discount modal */
.modal-content.card-base .modal-body label,
.modal-content.card-base form label {
  color: var(--text-primary);
  display: inline-flex;
  align-items: center;
  gap: 8px; /* text spacing without layout/size changes */
}

/* Separator uses themed border color */
.modal-content.card-base hr {
  border: none;
  border-top: var(--card-border);
  opacity: 1;
}

/* Input placeholder color for readability */
.modal-content.card-base .form-control::placeholder {
  color: var(--text-secondary);
  opacity: 1;
}

/**
 * Pins the Save Changes / Cancel action row to the bottom of the Bootstrap
 * modal-body so it remains visible on mobile (Capacitor Android / 360-414px
 * viewports) where the stacked form fields would otherwise push the buttons
 * below the fold.
 *
 * Approach B of exec-plan 2026-07-17-mobile-save-button-update-price-modal:
 * - `position: sticky` + `bottom: 0` keeps the row glued to the bottom of its
 *   scrolling ancestor (Bootstrap's default `.modal-body` is the scroll
 *   container when the dialog overflows).
 * - `background` matches the card surface so any underlying form fields that
 *   scroll under the row are visually covered.
 * - The `border-top` gives the row a clear separator from the form fields
 *   above so the sticky affordance is obvious on desktop too.
 * - Desktop layout (>= 992px) is unchanged because the row simply sits at the
 *   end of the slot body and only sticks when the content overflows.
 * - We deliberately do NOT modify `CustomModal.vue` (Approach C is deferred);
 *   `z-index: 2` keeps the row above form controls inside the same stacking
 *   context.
 */
.sticky-action-row {
  position: sticky;
  bottom: 0;
  background: var(--card-bg);
  padding-top: 8px;
  margin-top: 12px;
  border-top: 1px solid var(--card-border);
  z-index: 2;
}
</style>
